import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import {
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import { useMap } from "@mapcomponents/react-maplibre";
import proj4 from "proj4";
import { MapPointContext } from "../context/MapPointContext";
import { guessCoordinateCrs } from "../utils/coordinateCrsGuessing";
import { parseSingleDms, parseFullDmsPair, parseSingleFieldInput } from "../utils/coordinateDmsParser";
import {
    CRS_GUESS_MESSAGES,
    GERMANY_BBOX_BUFFERED,
} from "../config/coordinateSearchConfig";

const DEFAULT_MAP_ID = "map_1";
const COORDINATE_ZOOM_LEVEL = 16;
const FLY_TO_DURATION_MS = 1000;
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;
const WGS84_EPSG = "EPSG:4326";
const UTM32_EPSG = "EPSG:25832";
const UTM33_EPSG = "EPSG:25833";
const CRS_MAX_WIDTH_PX = 150;
const CRS_MIN_WIDTH_PX = 120;
const COORDINATE_FIELD_MIN_WIDTH_PX = 90;
const COORDINATE_PAIR_MIN_WIDTH_PX = COORDINATE_FIELD_MIN_WIDTH_PX * 2;

const CRS_OPTIONS = [
    { value: undefined, label: "" },
    { value: WGS84_EPSG, label: "WGS 84 " },
    { value: UTM32_EPSG, label: "UTM 32N " },
    { value: UTM33_EPSG, label: "UTM 33N " },
];

proj4.defs(
    UTM32_EPSG,
    "+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs +type=crs",
);
proj4.defs(
    UTM33_EPSG,
    "+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs +type=crs",
);

function dispatchMapClick(map, targetCoordinates) {
    const point = map.project(targetCoordinates);
    const canvas = map.getCanvas();
    const canvasRect = canvas.getBoundingClientRect();

    canvas.dispatchEvent(
        new MouseEvent("click", {
            bubbles: true,
            clientX: canvasRect.left + point.x,
            clientY: canvasRect.top + point.y,
        }),
    );
}

function parseCoordinateValue(value) {
    return Number.parseFloat(String(value).replace(",", "."));
}

export default function CoordinateSearch(props) {
    const mapHook = useMap({ mapId: props.mapId || DEFAULT_MAP_ID });
    const mapPointContext = useContext(MapPointContext);

    const [crs, setCrs] = useState();
    const [coordinateA, setCoordinateA] = useState("");
    const [coordinateB, setCoordinateB] = useState("");
    const [guessedCrs, setGuessedCrs] = useState(null);
    const [isSingleField, setIsSingleField] = useState(false);
    const [coordinateSingle, setCoordinateSingle] = useState("");
    const [guessAmbiguity, setGuessAmbiguity] = useState(false);
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const isMobileLayout = Boolean(props.isMobileLayout);

    const handleHelpOpen = (event) => setHelpAnchorEl(event.currentTarget);
    const handleHelpClose = () => setHelpAnchorEl(null);

    // Manual CRS takes priority over guessed; use effective CRS for label and conversion
    const effectiveCrs = crs ?? guessedCrs;
    const isWgs84 =
        effectiveCrs === WGS84_EPSG ||
        effectiveCrs === undefined ||
        effectiveCrs === null;

    const labels = useMemo(() => {
        if (isWgs84) {
            return {
                a: "Breite",
                b: "Laenge",
            };
        }

        return {
            a: "Rechtswert (X)",
            b: "Hochwert (Y)",
        };
    }, [isWgs84]);

    const setError = useCallback(
        (message) => {
            if (typeof props.setErrorMessage === "function") {
                props.setErrorMessage(message);
            }
        },
        [props],
    );

    const convertToWgs84 = useCallback(
        (valueA, valueB) => {
            if (isWgs84) {
                const latitude = parseCoordinateValue(valueA);
                const longitude = parseCoordinateValue(valueB);

                if (
                    Number.isNaN(latitude) ||
                    Number.isNaN(longitude) ||
                    Math.abs(latitude) > MAX_LATITUDE ||
                    Math.abs(longitude) > MAX_LONGITUDE
                ) {
                    return undefined;
                }

                return [longitude, latitude];
            }

            const x = parseCoordinateValue(valueA);
            const y = parseCoordinateValue(valueB);

            if (Number.isNaN(x) || Number.isNaN(y)) {
                return undefined;
            }

            return proj4(effectiveCrs, WGS84_EPSG, [x, y]);
        },
        [effectiveCrs, isWgs84],
    );

    const flyToAndSelect = useCallback(
        (targetCoordinates) => {
            const map = mapHook.map?.map;
            if (!map) {
                setError("Karte ist noch nicht bereit.");
                return;
            }

            const [lon, lat] = targetCoordinates;
            if (
                lat < GERMANY_BBOX_BUFFERED.minLat ||
                lat > GERMANY_BBOX_BUFFERED.maxLat ||
                lon < GERMANY_BBOX_BUFFERED.minLon ||
                lon > GERMANY_BBOX_BUFFERED.maxLon
            ) {
                setError(
                    "Koordinaten liegen außerhalb des zulässigen Bereichs (Deutschland).",
                );
                return;
            }

            setError(undefined);

            map.once("moveend", () => {
                dispatchMapClick(map, targetCoordinates);
            });

            map.flyTo({
                center: targetCoordinates,
                zoom: COORDINATE_ZOOM_LEVEL,
                duration: FLY_TO_DURATION_MS,
            });

            mapPointContext.setActivePoint(
                targetCoordinates,
                "coordinate-search",
            );
        },
        [mapHook.map, mapPointContext, setError],
    );

    // Reactively guess CRS whenever both coordinate fields have values and no manual CRS is set
    useEffect(() => {
        if (crs !== undefined) {
            // Manual CRS selected — clear any previous guess
            setGuessedCrs(null);
            setGuessAmbiguity(false);
            return;
        }

        if (!coordinateA || !coordinateB) {
            setGuessedCrs(null);
            setGuessAmbiguity(false);
            return;
        }

        const guessResult = guessCoordinateCrs(coordinateA, coordinateB);
        setGuessedCrs(guessResult.guessedCrs);
        setGuessAmbiguity(guessResult.ambiguity);
    }, [coordinateA, coordinateB, crs]);

    const handleCrsChange = useCallback(
    (newCrs) => {
        setCrs(newCrs);

        const activeCoords = mapPointContext.activePointFeature?.geometry?.coordinates;

        if (Array.isArray(activeCoords) && activeCoords.length === 2) {
            // Active point exists — reproject from its stored WGS84 coordinates
            if (newCrs === undefined) return; // clearing selection: keep fields as-is
            const [lon, lat] = activeCoords;
            if (newCrs === WGS84_EPSG) {
                setCoordinateA(lat.toFixed(6));
                setCoordinateB(lon.toFixed(6));
                return;
            }
            try {
                const [x, y] = proj4(WGS84_EPSG, newCrs, [lon, lat]);
                setCoordinateA(x.toFixed(2));
                setCoordinateB(y.toFixed(2));
            } catch { /* keep fields as-is */ }
            return;
        }

        // No active point: keep user input unchanged so CRS can be chosen manually.
    },
    [mapPointContext.activePointFeature],
);
  

    const handleCoordinateChange = useCallback(
        (field, rawValue) => {
            // Try to parse as a full DMS pair (both coords in one string)
            const fullPair = parseFullDmsPair(rawValue);
            if (fullPair !== null) {
                setCoordinateA(fullPair.lat.toFixed(6));
                setCoordinateB(fullPair.lon.toFixed(6));
                setCrs(WGS84_EPSG);
                return;
            }

            // Try to parse as a single DMS value
            const singleDecimal = parseSingleDms(rawValue);
            if (singleDecimal !== null) {
                if (field === "a") {
                    setCoordinateA(singleDecimal.toFixed(6));
                } else {
                    setCoordinateB(singleDecimal.toFixed(6));
                }
                setCrs(WGS84_EPSG);
                return;
            }

            // Plain input — pass through unchanged
            if (field === "a") {
                setCoordinateA(rawValue);
            } else {
                setCoordinateB(rawValue);
            }
        },
        [],
    );

    const handleSingleFieldChange = useCallback(
        (rawValue) => {
            const dmsParsed = parseFullDmsPair(rawValue);
            if (dmsParsed !== null) {
                const normalizedLat = dmsParsed.lat.toFixed(6);
                const normalizedLon = dmsParsed.lon.toFixed(6);
                setCoordinateSingle(`${normalizedLat} ${normalizedLon}`);
                setCoordinateA(normalizedLat);
                setCoordinateB(normalizedLon);
                setCrs(WGS84_EPSG);
                setIsSingleField(false);
                return;
            }

            const parsed = parseSingleFieldInput(rawValue);
            if (parsed !== null) {
                const normalizedLat = parsed.lat.toFixed(6);
                const normalizedLon = parsed.lon.toFixed(6);
                setCoordinateSingle(`${normalizedLat} ${normalizedLon}`);
                setCoordinateA(normalizedLat);
                setCoordinateB(normalizedLon);
                // Keep CRS automatic for plain numeric pairs so UTM can be detected.
                setCrs(undefined);
                setIsSingleField(false);
                return;
            }

            setCoordinateSingle(rawValue);
        },
        [],
    );

    const handleToggleInputMode = useCallback(() => {
        setIsSingleField((prev) => {
            const next = !prev;
            if (next) {
                // Switching to single-field: populate from current two-field values
                setCoordinateSingle(
                    coordinateA && coordinateB
                        ? `${coordinateA} ${coordinateB}`
                        : "",
                );
            } else {
                // Switching back to two-field: parse single field to repopulate A/B
                const parsed = parseSingleFieldInput(coordinateSingle);
                if (parsed !== null) {
                    setCoordinateA(parsed.lat.toFixed(6));
                    setCoordinateB(parsed.lon.toFixed(6));
                }
            }
            return next;
        });
    }, [coordinateA, coordinateB, coordinateSingle]);

    const handleCopy = useCallback(() => {
        if (!coordinateA || !coordinateB) return;
        navigator.clipboard.writeText(`${coordinateA} ${coordinateB}`);
    }, [coordinateA, coordinateB]);

    const handleSearch = useCallback(() => {
        const targetCoordinates = convertToWgs84(coordinateA, coordinateB);

        if (!targetCoordinates) {
            setError("Koordinaten sind ungültig oder unvollständig.");
            return;
        }

        flyToAndSelect(targetCoordinates);
    }, [coordinateA, coordinateB, convertToWgs84, flyToAndSelect, setError]);

    useEffect(() => {
        const activePointFeature = mapPointContext.activePointFeature;
        const coordinates = activePointFeature?.geometry?.coordinates;
        const source = activePointFeature?.properties?.source;

        // If point is cleared, reset CRS/guess state but keep coordinate fields
        // and clear coordinate fields to match the removed active point state
        if (!activePointFeature) {
            setCrs(undefined);
            setGuessedCrs(null);
            setGuessAmbiguity(false);
            setCoordinateA("");
            setCoordinateB("");
            setCoordinateSingle("");
            return;
        }

        // Only react to GPS updates
        if (source !== "gps") {
            return;
        }

        if (!Array.isArray(coordinates) || coordinates.length !== 2) {
            return;
        }

        const [longitude, latitude] = coordinates;
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return;
        }

        setCrs(WGS84_EPSG);
        setCoordinateA(latitude.toFixed(6)); // Breite
        setCoordinateB(longitude.toFixed(6)); // Laenge
        setError(undefined);
    }, [mapPointContext.activePointFeature, setError]);

    if (isMobileLayout) {
        return (
            <>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <FormControl variant="standard" sx={{ width: "100%" }}>
                    <InputLabel
                        id="coordinate-crs-select-label"
                        shrink={!!effectiveCrs}
                    >
                        CRS
                    </InputLabel>
                    <Select
                        value={effectiveCrs}
                        labelId="coordinate-crs-select-label"
                        onChange={(event) =>
                            handleCrsChange(event.target.value)
                        }
                    >
                        {CRS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Grid
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 1,
                        minWidth: 0,
                    }}
                >
                    <Tooltip title={isSingleField ? "Getrennte Felder" : "Einzelfeld"}>
                        <IconButton
                            onClick={handleToggleInputMode}
                            size="small"
                            sx={{ flexShrink: 0 }}
                        >
                            {isSingleField ? (
                                <ToggleOnOutlinedIcon sx={{ transform: "rotate(90deg)" }} />
                            ) : (
                                <ToggleOffOutlinedIcon sx={{ transform: "rotate(90deg)" }} />
                            )}
                        </IconButton>
                    </Tooltip>
                    {isSingleField ? (
                        <Grid
                            sx={{
                                flex: "2 1 0",
                                minWidth: `${COORDINATE_PAIR_MIN_WIDTH_PX}px`,
                            }}
                        >
                            <TextField
                                variant="standard"
                                label="Koordinaten"
                                value={coordinateSingle}
                                onChange={(event) =>
                                    handleSingleFieldChange(event.target.value)
                                }
                                sx={{ width: "100%" }}
                            />
                        </Grid>
                    ) : (
                        <>
                            <Grid sx={{ flex: "1 1 0", minWidth: 0 }}>
                                <TextField
                                    variant="standard"
                                    label={labels.a}
                                    value={coordinateA}
                                    onChange={(event) =>
                                        handleCoordinateChange("a", event.target.value)
                                    }
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                            <Grid sx={{ flex: "1 1 0", minWidth: 0 }}>
                                <TextField
                                    variant="standard"
                                    label={labels.b}
                                    value={coordinateB}
                                    onChange={(event) =>
                                        handleCoordinateChange("b", event.target.value)
                                    }
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid
                        sx={{
                            width: "40px",
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Tooltip title="Koordinaten kopieren">
                            <span>
                                <IconButton
                                    onClick={handleCopy}
                                    size="small"
                                    disabled={!coordinateA || !coordinateB}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid
                        sx={{
                            width: "40px",
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        {(guessAmbiguity && guessedCrs) ? (
                            <Tooltip
                                title={CRS_GUESS_MESSAGES.ambiguityWarning}
                            >
                                <WarningIcon
                                    sx={{
                                        color: "#e3c840",
                                        fontSize: "1.25rem",
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Koordinaten Suche Hilfe">
                                <IconButton onClick={handleHelpOpen}>
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>
                    <Grid
                        sx={{
                            width: "40px",
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <IconButton
                            aria-label="search-coordinate"
                            onClick={handleSearch}
                            size="small"
                        >
                            <SearchIcon style={{ color: "black" }} />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
            <Popover
                open={Boolean(helpAnchorEl)}
                anchorEl={helpAnchorEl}
                onClose={handleHelpClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <CoordinateSearchHelp />
            </Popover>
            </>
        );
    }

    return (
        <Grid
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                flexGrow: 1,
                minWidth: 0,
            }}
        >
            <Tooltip title={isSingleField ? "Getrennte Felder" : "Einzelfeld"}>
                <IconButton
                    onClick={handleToggleInputMode}
                    size="small"
                    sx={{ flexShrink: 0 }}
                >
                    {isSingleField ? (
                        <ToggleOnOutlinedIcon sx={{ transform: "rotate(90deg)" }} />
                    ) : (
                        <ToggleOffOutlinedIcon sx={{ transform: "rotate(90deg)" }} />
                    )}
                </IconButton>
            </Tooltip>
            {isSingleField ? (
                <Grid
                    sx={{
                        flex: "4 1 0",
                        minWidth: `${COORDINATE_PAIR_MIN_WIDTH_PX}px`,
                    }}
                >
                    <TextField
                        variant="standard"
                        label="Koordinaten"
                        value={coordinateSingle}
                        onChange={(event) => handleSingleFieldChange(event.target.value)}
                        sx={{ width: "100%" }}
                    />
                </Grid>
            ) : (
                <>
                    <TextField
                        variant="standard"
                        label={labels.a}
                        value={coordinateA}
                        onChange={(event) => handleCoordinateChange("a", event.target.value)}
                        sx={{
                            flex: "2 1 0",
                            minWidth: `${COORDINATE_FIELD_MIN_WIDTH_PX}px`,
                        }}
                    />
                    <TextField
                        variant="standard"
                        label={labels.b}
                        value={coordinateB}
                        onChange={(event) => handleCoordinateChange("b", event.target.value)}
                        sx={{
                            flex: "2 1 0",
                            minWidth: `${COORDINATE_FIELD_MIN_WIDTH_PX}px`,
                        }}
                    />
                </>
            )}
            <Grid
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                }}
            >
                <Tooltip title="Koordinaten kopieren">
                    <span>
                        <IconButton
                            onClick={handleCopy}
                            size="small"
                            disabled={!coordinateA || !coordinateB}
                            sx={{ flexShrink: 0 }}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <FormControl
                    variant="standard"
                    sx={{ minWidth: `${CRS_MIN_WIDTH_PX}px`, maxWidth: `${CRS_MAX_WIDTH_PX}px` }}
                >
                    <InputLabel
                        id="coordinate-crs-select-label"
                        shrink={!!effectiveCrs}
                    >
                        CRS
                    </InputLabel>
                    <Select
                        value={effectiveCrs}
                        labelId="coordinate-crs-select-label"
                        onChange={(event) =>
                            handleCrsChange(event.target.value)
                        }
                    >
                        {CRS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {guessAmbiguity && guessedCrs ? (
                    <Tooltip title={CRS_GUESS_MESSAGES.ambiguityWarning}>
                        <WarningIcon
                            sx={{
                                color: "#e3c840",
                                fontSize: "1.25rem",
                                flexShrink: 0,
                            }}
                        />
                    </Tooltip>
                ) : (
                    <Tooltip title="Koordinaten Suche Hilfe">
                        <IconButton onClick={handleHelpOpen} sx={{ flexShrink: 0 }}>
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Grid>

            <Popover
                open={Boolean(helpAnchorEl)}
                anchorEl={helpAnchorEl}
                onClose={handleHelpClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <CoordinateSearchHelp />
            </Popover>

            <IconButton
                aria-label="search-coordinate"
                onClick={handleSearch}
                sx={{ flexShrink: 0 }}
            >
                <SearchIcon style={{ color: "black" }} />
            </IconButton>
        </Grid>
    );
}

function CoordinateSearchHelp() {
    return (
        <Grid sx={{ p: 2, maxWidth: 380 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Koordinatensuche
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Komma oder Punkt als Dezimaltrennzeichen sind erlaubt. Das
                Koordinatensystem wird automatisch erkannt, kann aber manuell
                über CRS gewählt werden.
            </Typography>

            <Grid
                sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Eingabemodus
                </Typography>
                <Typography variant="body2" component="div">
                    Zum Umschalten zwischen Einzelfeld und Doppelfeld das Toggle
                    verwenden:
                    <ToggleOffOutlinedIcon
                        sx={{
                            ml: 0.5,
                            mr: 0.25,
                            fontSize: "1rem",
                            transform: "rotate(90deg)",
                            verticalAlign: "text-bottom",
                        }}
                    />
                    / <ToggleOnOutlinedIcon
                        sx={{
                            ml: 0.25,
                            mr: 0.5,
                            fontSize: "1rem",
                            transform: "rotate(90deg)",
                            verticalAlign: "text-bottom",
                        }}
                    />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Im Einzelfeld sind z. B. DMS oder "Breite Laenge" moeglich.
                    Nach erfolgreicher Pruefung werden Werte in Dezimalgrad
                    umgewandelt und ins Doppelfeld uebernommen.
                </Typography>
            </Grid>

            <Grid
                sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    WGS 84 (EPSG:4326)
                </Typography>
                <Typography variant="body2">Dezimalgrad</Typography>
                <Typography variant="body2" color="text.secondary">
                    Feld 1: Breite (Lat), Feld 2: Laenge (Lon)
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Beispiel: <em>51.3396, 12.3713</em>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    DMS wird ebenfalls akzeptiert, z. B.
                    <em> 50°23'59.93"N 10°13'07.99"E</em>
                </Typography>
            </Grid>

            <Grid
                sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    UTM 32N (EPSG:25832)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Meter, Feld 1: Rechtswert (X), Feld 2: Hochwert (Y)
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Beispiel: <em>388906, 5686385</em>
                </Typography>
            </Grid>

            <Grid
                sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    UTM 33N (EPSG:25833)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Meter, Feld 1: Rechtswert (X), Feld 2: Hochwert (Y)
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Beispiel: <em>300000, 5700000</em>
                </Typography>
            </Grid>
        </Grid>
    );
}
